import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  signal,
  inject,
  effect,
  untracked,
} from '@angular/core';
import { KingNewService } from '../../king-new.service';
import { KingUser } from '../../interfaces/king.interface';
import { KingWeaponComponent } from '../king-weapon/king-weapon.component';
import { SvgIconComponent } from 'angular-svg-icon';
import { KingUserAvatarComponent } from '../king-user-avatar/king-user-avatar.component';
import { KingGlowSvgComponent } from '../../svg/king-glow-svg.component';
import { NgTemplateOutlet } from '@angular/common';
import { ModalService } from '@features/modals/services/modal.service';
import { sleep } from '@core/utils/sleep.util';
/** Queued weapon animation */
interface QueuedWeaponAnimation {
  animation: string;
}

@Component({
  selector: 'app-king-side-top',
  templateUrl: './king-side-top.component.html',
  styleUrls: ['./king-side-top.component.scss'],
  standalone: true,
  imports: [
    KingWeaponComponent,
    SvgIconComponent,
    KingUserAvatarComponent,
    KingGlowSvgComponent,
    NgTemplateOutlet,
  ],
  host: {
    '[class.king]': '!isChallenger()',
    '[class.challenger]': 'isChallenger()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KingSideTopComponent {
  readonly isChallenger = input<boolean>(false);
  readonly user = input<KingUser | null>(null);
  readonly role = computed<'king' | 'challenger'>(() => this.isChallenger() ? 'challenger' : 'king');
  readonly iconSrc = computed(() =>
    this.isChallenger() ? 'assets/icons/king/sword.svg' : 'assets/icons/games/king.svg'
  );
  readonly attackAnimation = signal<string | null>(null);

  readonly kingService = inject(KingNewService);
  private readonly modalService = inject(ModalService);

  openDepositModal() {
    this.modalService.openModal('DepositKingModal');
  }

  private lastProcessedHitIndex = -1;
  private isProcessingQueue = false;
  private lastGameId: number | null = null;

  // Weapon animation queue — see KingNewService JSDoc for the 3-queue architecture overview.
  private animationQueue: QueuedWeaponAnimation[] = [];

  constructor() {
    // Effect to reset state on new game
    effect(() => {
      const game = this.kingService.game();
      const gameId = game?.gameid ?? null;

      untracked(() => {
        if (gameId !== this.lastGameId) {
          this.lastGameId = gameId;
          this.lastProcessedHitIndex = this.kingService.hits().length;
          this.animationQueue = [];
          this.isProcessingQueue = false;
          this.attackAnimation.set(null);
        }
      });
    });

    // Effect to handle incoming hits
    effect(() => {
      const hits = this.kingService.hits();
      const hitCount = hits.length;
      const lastHit = hits.at(-1);
      const role = this.role();

      untracked(() => {
        if (
          lastHit &&
          hitCount > this.lastProcessedHitIndex &&
          lastHit.by === role
        ) {
          this.lastProcessedHitIndex = hitCount;
          this.queueWeaponAnimation(lastHit.animation);
        }
      });
    });
  }

  /**
   * Queue a weapon animation for processing.
   */
  private queueWeaponAnimation(animation: string): void {
    this.animationQueue.push({ animation });

    if (!this.isProcessingQueue) {
      this.processNextAnimation();
    }
  }

  /**
   * Process the next animation in the queue.
   */
  private async processNextAnimation(): Promise<void> {
    if (this.animationQueue.length === 0) {
      this.isProcessingQueue = false;
      return;
    }

    this.isProcessingQueue = true;
    const item = this.animationQueue.shift()!;

    await this.playWeaponAnimation(item.animation);

    // Process next animation in queue
    this.processNextAnimation();
  }

  private async playWeaponAnimation(animation: string): Promise<void> {
    this.attackAnimation.set(animation);
    await sleep(this.kingService.GUN_ANIMATION_DURATION);
    this.attackAnimation.set(null);
  }
}
